import RobotOnMap from 'components/RobotOnMap/RobotOnMap'
import SetPoseFloatingButton from 'components/RobotOnMap/SetPoseFloatingButton/SetPoseFloatingButton'
import RobotOnMapEditable from 'components/RobotOnMapEditable/RobotOnMapEditable'
import { Point } from 'geojson'
import { useIsMobile } from 'hooks/useIsMobile'
import GausiumMapGeoPinsOnMap, {
  GausiumMapGeoPin,
} from 'pages/Facility/FacilityView/GausiumMapGeoPinsOnMap/GausiumMapGeoPinsOnMap'
import MissionMap from 'pages/Mission/MissionView/MissionMap/MissionMap'
import LaserscanLayer from 'pages/Robot/RobotView/Layers/LaserscanLayer'
import React, { forwardRef, useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { selectRobotMapLocalization } from 'store/selectors/robot'
import { IMapboxElement, IMission, RootState } from 'utils/interfaces'
import { buildLaserscanOverlayItem } from 'utils/laserscanOverlayItem'
import { GEO_MAP_LEFT_PANEL_INSET_PX } from 'utils/scenarioMapLayout'
import { isSetPoseButtonDisabled } from 'utils/setPoseUi'
import { VENDOR } from 'utils/vendorDisplayName'

interface MissionControlMapProps {
  missions?: IMission[]
  highlightedMissionId?: number
  gausiumMapPins?: GausiumMapGeoPin[]
  gausiumPinHighlightedId?: number
  isGausium?: boolean
  hideRobotIcon?: boolean
  onGausiumPinClick?: (id: number) => void
  setPoseMode?: boolean
  setSetPoseMode?: (val: boolean | ((prev: boolean) => boolean)) => void
  setPosePosition?: Point
  setPoseYaw?: number
  setSetPosePosition?: (val: Point | undefined) => void
  setSetPoseYaw?: (val: number | undefined) => void
  laserscanActive?: boolean
  laserscanData?: any
  toggleLaserscan?: () => void | Promise<void>
  /** False when the robot has no active mission: keep the light map shell. */
  hasActiveMission?: boolean
}

/** Mounted only while laserscan is on, so idle Mission Control does not subscribe to live pose. */
const MissionControlLaserscanLive = ({ scan }: { scan: any }) => {
  const livePosition = useSelector((state: RootState) => state.robot.telemetry?.livePosition)
  const quaternion = useSelector((state: RootState) => state.robot.telemetry?.quaternion)
  const lastCoordinates = useSelector((state: RootState) => state.robot.robotInfo?.lastPosition?.coordinates)
  const lastOrientation = useSelector((state: RootState) => state.robot.robotInfo?.lastOrientation)
  const poseFrame = useSelector((state: RootState) => state.robot.robotInfo?.poseFrame)
  const wheelBaseM = useSelector((state: RootState) => state.robot.robotInfo?.wheelBaseM)
  return (
    <LaserscanLayer
      show
      scan={scan}
      robotPosition={livePosition ?? lastCoordinates}
      robotOrientation={quaternion ?? lastOrientation}
      poseFrame={poseFrame}
      wheelBaseM={wheelBaseM}
    />
  )
}

const MissionControlMap = forwardRef<IMapboxElement, MissionControlMapProps>(function MissionControlMap(
  props: MissionControlMapProps,
  ref: React.ForwardedRef<IMapboxElement>
) {
  const mapRef = useRef<IMapboxElement | null>(null)
  const robotLoc = useSelector(selectRobotMapLocalization, shallowEqual)
  const isMobile = useIsMobile()
  const showLidarMapOption = robotLoc.vendor === VENDOR.AUTONOMICS
  const panelOffset = isMobile ? 0 : GEO_MAP_LEFT_PANEL_INSET_PX
  const searchRight = isMobile ? 16 : 360

  const setRefs = (instance: IMapboxElement | null) => {
    mapRef.current = instance
    if (typeof ref === 'function') ref(instance)
    else if (ref) (ref as React.MutableRefObject<IMapboxElement | null>).current = instance
  }

  const { laserscanActive, toggleLaserscan } = props
  const extraOverlayItems = React.useMemo(() => {
    const items = []
    const laserscanItem = buildLaserscanOverlayItem({
      localizationMode: robotLoc.localizationMode,
      laserscanActive: !!laserscanActive,
      robotId: robotLoc.robotId,
      onToggle: () => toggleLaserscan?.(),
    })
    if (robotLoc.vendor === VENDOR.AUTONOMICS && laserscanItem) {
      items.push(laserscanItem)
    }
    return items
  }, [robotLoc.vendor, robotLoc.robotId, robotLoc.localizationMode, laserscanActive, toggleLaserscan])

  return (
    <MissionMap
      ref={setRefs}
      missions={props.missions}
      highlightedMissionId={props.highlightedMissionId}
      showLidarMapOption={showLidarMapOption}
      disableOverlayControl={!!props.isGausium}
      disableRuler={!!props.isGausium}
      disableSearch={true}
      searchRight={searchRight}
      panelOffset={panelOffset}
      extraOverlayItems={extraOverlayItems}
      interaction="view"
      mountMissionLayers={!!props.hasActiveMission}
    >
      {!props.hideRobotIcon && (
        <RobotOnMap
          map={mapRef.current?.getMap()}
          highlightedMissionId={props.highlightedMissionId}
          offset={panelOffset}
          disableCenterButton={props.setPoseMode}
          setPoseMode={props.setPoseMode}
        />
      )}
      {props.setPoseMode && (
        <RobotOnMapEditable
          map={mapRef.current?.getMap()}
          position={props.setPosePosition}
          yaw={props.setPoseYaw}
          onPositionChanged={props.setSetPosePosition}
          onYawChanged={props.setSetPoseYaw}
        />
      )}
      {props.laserscanActive ? (
        <MissionControlLaserscanLive scan={props.laserscanData} />
      ) : (
        <LaserscanLayer show={false} scan={props.laserscanData} />
      )}
      {props.setSetPoseMode && (
        <SetPoseFloatingButton
          laserscanActive={!!props.laserscanActive}
          setPoseMode={!!props.setPoseMode}
          setSetPoseMode={props.setSetPoseMode}
          disabled={isSetPoseButtonDisabled(robotLoc.localizationMode, !!robotLoc.lidarMapName)}
          localizationMode={robotLoc.localizationMode}
        />
      )}
      {props.isGausium && props.gausiumMapPins?.length ? (
        <GausiumMapGeoPinsOnMap
          map={mapRef.current?.getMap()}
          pins={props.gausiumMapPins}
          highlightedId={props.gausiumPinHighlightedId}
          onPinClick={props.onGausiumPinClick}
        />
      ) : null}
    </MissionMap>
  )
})

export default MissionControlMap
